import React, { useEffect, useMemo, useState } from "react";
import { formatMoneyIntl } from "../lib/formatMoney"; // relative: must REALLY resolve under Jest (no "@/" alias there)
import { currentRef } from "../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
import { currentPromotionRef } from "../lib/promotionRef";
// --- crm-module END ---
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { checkoutReturnFields, tenantHref } from "@/lib/tenantPath";
import { useLanguage, useT } from "@/lib/LanguageContext";
import PromoField from "@/components/checkout/PromoField";
import GiftCardField from "@/components/checkout/GiftCardField";
// Relative, not "@/...": a real module under Jest, no alias there.
import MarketingOptIn from "../components/consent/MarketingOptIn";
import { useTenant, useTenantHomePath } from "@/lib/TenantContext";
import { useAuth } from "@/lib/AuthContext";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, ArrowRight, ArrowLeft, Clock, CheckCircle2, Globe, Video } from "lucide-react";
import { motion } from "framer-motion";
import { format, parse, startOfMonth } from "date-fns";
import { readPending, writePending, clearPending } from "@/lib/pendingBooking";
// BUG-193 — the calendar wiring (classNames, day-key format, locale table) is
// now shared with public/ManageBooking.jsx's reschedule step, which needs the
// same date-first picker. Relative, not "@/...": ManageBooking.jsx's own test
// renders the real react-day-picker grid with no mocks at all, so this file
// cannot be reached only through an aliased path — see its own docstring.
// `Calendar` itself (imported above, unchanged) is handed down as a prop.
import AvailabilityCalendar, { DAY_KEY_FMT } from "../components/booking/AvailabilityCalendar";
// BUG-195 — sibling of pendingBooking.js above, for the OTHER round trip that can unmount
// this page: a Google sign-in on a tenant host (see the module's own docstring). Relative,
// not "@/...": every OTHER Reserver.jsx test suite mocks "@/lib/pendingBooking" but knows
// nothing of this new module, and a real relative import needs no mock at all under Jest
// (no "@" alias there) — same reasoning as useCheckoutQuote/FieldError below.
import { writeBookingDraft, readAndClearBookingDraft } from "../lib/pendingBookingDraft";
// Relative, not "@/...": real module under Jest, no alias there.
import FieldError from "../components/ui/FieldError";
// Relative, for the same reason: the shared pre-payment disclosure (BUG-048/BUG-054) and
// the hook that asks the SERVER what this appointment will cost.
import useCheckoutQuote from "../lib/useCheckoutQuote";
import PriceBreakdown from "../components/checkout/PriceBreakdown";
import ConsultRateLabel from "./ConsultRateLabel";
// The per-minute predicates live beside the label that renders them: the marketplace
// card reads the same four answers off the same wire (see ./consultPricing).
import { consultOf, isPerMinute, isUnbookablePerMinute } from "./consultPricing";
// One sentence per gateway code, in the `common` namespace. Imported rather than
// re-translated under a `booking.*` key: `PROVIDER_NOT_BILLABLE` is the SAME refusal the
// consult surface renders (ConsultBooking.jsx), and a second copy of it in a second
// dictionary is how two screens start explaining one fact two ways.
import { gatewayErrorText } from "../gateway/errorText";
// The gateway's CLOSED code set, so membership is asked rather than enumerated: every
// code in it already has a `gw.error.*` sentence in three languages, and listing a subset
// of them by hand is how `PRICING_NOT_SET` came to be printed raw while its translated
// sentence sat unused in the dictionary.
import { ERROR_CODES } from "../gateway/contract";

/**
 * checkoutEmail — the email a checkout payload is actually sent with once the
 * buyer is authenticated: the signed-in identity always wins over whatever is
 * typed in the (now disabled) email field, falling back to the typed value only
 * when there is no user yet (never reached once the transactional gate below is
 * in place, kept for callers that might not have gated). See
 * docs/auth-and-admin/client-otp-auth-contract.md.
 */
export const checkoutEmail = (user, typedEmail) => (user && user.email) || typedEmail || "";

// Formatting locale per app language. The FALLBACK is English, not French: `en` is
// BASE_LANG (i18n/index.js) and the language every missing string degrades to, so a
// `lang` this map does not know must not silently produce "50,00 $" in an English page.
const FMT_LOCALES = { en: "en-CA", es: "es-ES", fr: "fr-CA" };
const FMT_FALLBACK = "en-CA";

const fmtDayLong = (d, lang) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleDateString(FMT_LOCALES[lang] || FMT_FALLBACK, {
    weekday: "long", day: "numeric", month: "long",
  });
};

const fmtTime = (d, lang) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleTimeString(FMT_LOCALES[lang] || FMT_FALLBACK, {
    hour: "2-digit", minute: "2-digit",
  });
};

const fmtPrice = (cents, currency, lang) => formatMoneyIntl(cents, currency, lang);

// The server states whether a service can be paid online (`payable_online`, currency rule
// only for booking: a price of 0 is FREE by contract). A service held in a currency the
// platform no longer settles is shown honestly and cannot be chosen — the checkout would
// refuse it. `!== false`: a payload predating the flag is judged bookable as before.
// `s` is always a SERVICE row: a chooser row (GET /public/booking/services) or the
// `service` object GET /availability answers with — both carry the flag at top level.
const isUnpayableOnline = (s) => s?.payable_online === false;

// A priced service: THE SERVICE'S OWN price, read live from the catalogue (never frozen
// anywhere on the page). 0 => a free appointment, no payment. A per-minute service keeps
// 0 here by contract — its money is the rate card (`consultPricing.js`).
const priceCentsOf = (svc) => Number(svc?.price_cents || 0);

// Can the times of this service be offered at all? The chooser already keeps both of
// these off its tiles; this is the same rule applied to the service GET /availability
// answers with, because that read is fresher than the chooser's: a practitioner who
// removed the rate card, or whose currency changed, between the two loads must not have
// times offered that the booking would refuse after the whole form (PRICING_NOT_SET,
// CURRENCY_NOT_SETTLEABLE).
const isOfferable = (svc) => Boolean(svc) && !isUnbookablePerMinute(svc) && !isUnpayableOnline(svc);

/** Same instant? Starts cross the wire as ISO strings whose spelling may differ ("Z" vs
 *  "+00:00", with or without milliseconds), so they are compared as instants. */
const sameInstant = (a, b) => {
  const ta = new Date(a).getTime();
  return Number.isFinite(ta) && ta === new Date(b).getTime();
};

// Mirrors the backend contract (modules/booking/schemas.py BookingCreate): firstName
// and lastName are both required (min 1, max 80 — lastName had NO client-side check at
// all before this, so a blank one always round-tripped to a 422), phone max 40, notes
// max 2000. `requireEmail` is false once the buyer is authenticated: the payload's
// email is then the signed-in identity (checkoutEmail() below), never the (disabled)
// typed field, so validating the field the server will not read would reject a form the
// server would have accepted. Zod's `message` is deliberately an i18n KEY, not prose —
// resolved through `t` at the call site (useT("public")), same as every other user-facing
// string here; this schema itself has no hook and stays a plain, testable function (same
// discipline as lib/checkoutErrors.js).
const bookingFormSchema = (requireEmail) =>
  z.object({
    firstName: z.string().trim()
      .min(1, "booking.validation.firstNameRequired")
      .max(80, "booking.validation.firstNameTooLong"),
    lastName: z.string().trim()
      .min(1, "booking.validation.lastNameRequired")
      .max(80, "booking.validation.lastNameTooLong"),
    email: requireEmail
      ? z.string().trim()
        .min(1, "booking.validation.emailRequired")
        .max(254, "booking.validation.emailInvalid")
        .email("booking.validation.emailInvalid")
      : z.string(),
    phone: z.string().trim().max(40, "booking.validation.phoneTooLong"),
    notes: z.string().trim().max(2000, "booking.validation.notesTooLong"),
  });

/**
 * @returns {object|null} `{field: i18nKey}` (one message per field, first issue wins) or
 *   `null` when the form is valid.
 */
const validateBookingForm = (form, requireEmail) => {
  const result = bookingFormSchema(requireEmail).safeParse(form);
  if (result.success) return null;
  const fieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0];
    if (!fieldErrors[field]) fieldErrors[field] = issue.message;
  }
  return fieldErrors;
};

/**
 * WHY A BOOKING WAS REFUSED, as one sentence in the patient's own language (BUG-208).
 *
 * THE TABLE IS BOOKING'S OWN, and it is keyed by the refusal CODE only — never by the
 * server's `message`. Every `{code, message}` envelope on this path
 * (`payments/service.py:91`, `promos.py:397`, the 503 `PAYMENTS_UNAVAILABLE` raise)
 * carries an English developer diagnostic in `message` — "this practice prices in USD,
 * not CAD", "STRIPE_API_KEY is absent or is the demo sentinel" — and putting that in
 * front of an FR/ES patient is the defect `lib/checkoutErrors.js` and `lib/apiErrors.js`
 * were both written to prevent.
 *
 * WHY A LOCAL TABLE RATHER THAN `checkoutErrorMessage`. Two reasons, and the second is
 * the load-bearing one:
 *   1. Half of these codes are BOOKING's alone (`TIME_UNAVAILABLE`, `PAYMENT_HOLD_EXPIRED`,
 *      `ALREADY_BOOKED`…). `lib/apiErrors.js`'s own rule is that a code a caller
 *      maps locally stays out of shared infra, so the wording of one code cannot drift
 *      between two screens — the arrangement `admin/booking/calendarUtils.js`
 *      (ERROR_CODE_KEYS) already uses for the practitioner's half of this same domain.
 *   2. `checkoutErrorMessage`'s LAST RESORT is `extractApiError`, which returns
 *      `detail.message` for an object refusal it cannot name. Delegating the tail to it
 *      would therefore reintroduce exactly the English-prose leak above for the four
 *      server-defect codes (`PRODUCT_TYPE_UNKNOWN`, `SETTINGS_INVALID`, `FULLY_COVERED`,
 *      and any future addition). Deciding the tail here is what lets an unnameable
 *      refusal degrade to the generic instead.
 * The money codes below therefore point at the SAME i18n keys `checkoutErrors.js` uses
 * for the same refusals: a declined gift card on an appointment is the same fact as on a
 * product, and the two storefronts say it with one string, not two.
 */
const BOOKING_ERROR_KEYS = Object.freeze({
  // ── the time ────────────────────────────────────────────────────────────────────
  ALREADY_BOOKED: "booking.alreadyBooked",
  // A payment that was started then abandoned is RESUMED (the server answers with a
  // `checkout_url`, handled by the caller). These two are the cases where it is not, and
  // each deserves its own sentence: "already paid" and "hold expired" do not have the
  // same sequel for the patient.
  BOOKING_ALREADY_PAID: "booking.alreadyPaid",
  PAYMENT_HOLD_EXPIRED: "booking.holdExpired",
  // The start is not offered any more — someone else's booking took its minutes, or the
  // practitioner closed that time — between the times being shown and this submit
  // (`availability.assert_offered`, or the claims' unique index losing a race). `book()`
  // does not leave it in the form: it sends the client back to a FRESH read of the times,
  // where this sentence is shown (see TIMES_REFRESH_CODES below).
  TIME_UNAVAILABLE: "booking.timeUnavailable",
  // ── the catalogue ───────────────────────────────────────────────────────────────
  // Not a code: `service.py` answers an unknown or deactivated service with this English
  // literal (404) — on the times read as on the submit. Mapped, because printed verbatim
  // it puts English prose on a French page; the remedy is another SERVICE.
  SERVICE_NOT_FOUND: "booking.serviceUnavailable",
  // ── the money, shared with the other storefronts (lib/checkoutErrors.js) ────────
  // One key for all seven of the server's promo refusals, matching the backend, which
  // deliberately does not say which one it is (`promos.py:68-71` — telling the buyer
  // would turn the endpoint into an oracle for enumerating a practice's codes).
  PROMO_INVALID: "promo.invalid",
  GIFTCARD_DECLINED: "checkout.error.giftcardDeclined",
  CURRENCY_NOT_SETTLEABLE: "checkout.error.currencyNotSettleable",
  // Two codes, one fact for the patient: no session could be opened, so nothing was ever
  // presented to a card. The sentence must NOT say "declined" — misattributing a
  // platform outage to the patient's card is worse on a money path than saying nothing.
  PAYMENTS_UNAVAILABLE: "checkout.error.providerUnavailable",
  PAYMENT_PROVIDER_UNAVAILABLE: "checkout.error.providerUnavailable",
  // `orders.quote_hosted_order` refuses a non-positive or out-of-range total. The same
  // sentence the tile itself shows for a price that cannot be charged online (see
  // `checkout.error.invalidPrice` below in the grid), so the two agree.
  AMOUNT_INVALID: "checkout.error.invalidPrice",
  // ── the platform ────────────────────────────────────────────────────────────────
  // `request_limits.RULES` "booking-create": 10 per 60 s. The middleware writes its own
  // body, so this arrives as a bare string and never passes a FastAPI handler.
  RATE_LIMITED: "booking.rateLimited",
  // The practice stopped being served between this page loading and this submit. The
  // `common` dictionary's `errors.tenantSuspended` is the PRACTITIONER's wording
  // ("changes cannot be saved until it is reactivated") and would be nonsense here.
  TENANT_SUSPENDED: "booking.practiceUnavailable",
  // The practice's plan ceiling on bookings per month is reached (409, contract
  // "Limits"). The practitioner's plan is none of the patient's business, so the patient
  // gets the same neutral sentence as a practice that is not being served: online booking
  // is not available right now. The practitioner sees the meter on Billing.
  PLAN_LIMIT_REACHED: "booking.practiceUnavailable",
  // Same fact, same sentence: what is booked is no longer in the practice's plan (video
  // rooms after a downgrade — 403, answered to anonymous callers too), or not built at
  // all (503). The ETA a 503 may carry is a platform roadmap fact, not the patient's.
  CAPABILITY_NOT_IN_PLAN: "booking.practiceUnavailable",
  CAPABILITY_NOT_BUILT: "booking.practiceUnavailable",
});

/**
 * The refusals whose remedy is "pick another time": the page returns the client to the
 * times step and re-reads them, rather than leaving a dead tile selected under the form.
 */
const TIMES_REFRESH_CODES = new Set(["TIME_UNAVAILABLE"]);

/** The refusal CODE of an axios error, whichever envelope it travelled in — a bare
 *  string (`detail: "TIME_UNAVAILABLE"`) or the money path's `{code, message}`. */
const refusalCode = (e) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.code;
};

/**
 * @param {object} e         the axios error
 * @param {function} t       translator bound to the "public" namespace
 * @param {function} tc      translator bound to the "common" namespace (gw.error.*)
 * @returns {string} a sentence, never a wire code and never English prose in an FR/ES page
 */
export const bookingRefusalMessage = (e, t, tc) => {
  const detail = e?.response?.data?.detail;
  // A FastAPI/Pydantic 422 answers a LIST of {type, loc, msg}. Its `msg` is Pydantic's
  // own English validation prose ("String should have at least 1 character") — a
  // developer diagnostic, never meant for a patient — so it is deliberately DISCARDED,
  // exactly as `lib/apiErrors.js`'s 422 branch discards it. What replaces it is not the
  // page-level generic but a sentence that names the KIND of problem: the details in the
  // form are what need another look. Matched FIRST: an array is also an object.
  if (Array.isArray(detail)) return t("booking.invalidDetails");
  // ONE read for both envelopes a refusal can travel in (`refusalCode`), so a later
  // change of envelope on one route cannot silently stop a code from being recognised.
  const code = refusalCode(e);
  const key = code ? BOOKING_ERROR_KEYS[code] : undefined;
  if (key) return t(key);
  // A code from the gateway's closed set keeps the sentence the gateway already has for
  // it, in the `common` namespace — the same one ConsultBooking.jsx shows one door later.
  if (code && ERROR_CODES.includes(code)) return gatewayErrorText(code, tc);
  // An OBJECT refusal whose code this build cannot NAME. Its `message` is a developer
  // diagnostic, so the generic is the honest answer — reached deliberately here rather
  // than by falling through to a last resort that would print that message.
  if (detail && typeof detail === "object") return t("booking.genericError");
  // A BARE STRING this build has never heard of: a 4xx from a newer server must still
  // say SOMETHING rather than nothing (pinned by Reserver.providerNotBillable.test.js).
  // The residual English-prose risk is the price of that, and it shrinks with every
  // literal added to the table above.
  if (typeof detail === "string" && detail) return detail;
  // No `detail` at all — a dropped connection, a CORS failure. Nothing was diagnosed, so
  // nothing specific may be claimed.
  return t("booking.genericError");
};

/**
 * THE SHAPES A PRACTICE'S CATALOGUE CAN HAVE — three of them once it has answered — and
 * what each one means for the client's choice of service. Under open hours the SERVICE is
 * what gives a time its length (docs/booking-module/open-hours-design.md): no time exists
 * until a service is chosen, and `POST /api/bookings` requires one. So "nothing to choose"
 * and "nothing bookable" are the same dead end now — LOOP-202's chooser with no tiles
 * cannot come back, because a catalogue with nothing bookable never renders a chooser.
 *   • LOADING — GET /public/booking/services has not answered yet.
 *   • NONE    — nothing is bookable: the catalogue is empty (or unreachable), or every
 *               service in it is an unbookable per-minute consult (D-2). Nothing is
 *               fetched and the client gets a dead-end sentence.
 *   • ONE     — exactly one bookable service, payable online. The chooser STEP is
 *               skipped, but the CHOICE is still made for the client (BUG-022
 *               decision 3) and its times are read straight away.
 *   • CHOOSER — the client chooses: 2+ bookable services, or a single one that cannot be
 *               paid online (its tile is shown honestly, disabled — auto-choosing it
 *               would offer times whose checkout refuses).
 */
const CATALOGUE = Object.freeze({
  LOADING: "loading",
  NONE: "none",
  ONE: "one",
  CHOOSER: "chooser",
});

export default function Reserver() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user, ensureAuthed, authDialog, bridgeNonce } = useRequireAuth();
  // BUG-039: "/" is the SaaS marketing page on the platform apex, not this tenant's site.
  const homePath = useTenantHomePath();
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  // Moved up from its original spot beside `useCheckoutQuote` below: BUG-195's draft
  // read/write both need it, and both happen before that point in the component.
  const { slug: tenantSlug } = useTenant();

  // SERVICE FIRST, THEN A TIME.
  //   • `services`  — the BOOKABLE catalogue; null until loaded (≠ "the practice has none")
  //   • `serviceId` — the CLIENT'S pick: set by the chooser click, preselected by the
  //                    page's `?service=<id>` (a pathway's self-paced live step links here
  //                    that way, docs/pathways-module/pathways-api-contract.md §10.7), or
  //                    restored from a BUG-195 draft, which wins over the query because it
  //                    is the client's later choice. It only counts once it names a
  //                    choosable row of `services`, so an unknown, inactive or unlisted id
  //                    leaves the normal chooser up; a ONE catalogue needs no pick at all
  //                    (`chosenService` below).
  //   • `catalogue` — which of the shapes above this practice turned out to have.
  const [services, setServices] = useState(null);
  // Read once, from the URL the page was opened at (the ManageBooking.jsx convention): a
  // link to /reserver?service=… is a fresh page, never a change of query under this mount.
  const [serviceId, setServiceId] = useState(
    () => new URLSearchParams(window.location.search).get("service") || null,
  );
  const [catalogue, setCatalogue] = useState(CATALOGUE.LOADING);
  // THE TIMES OF THE CHOSEN SERVICE — GET /availability?service_id=… →
  // `{service: {...advertised service}, starts: [{start: ISO UTC, held_by_you: bool}]}`,
  // kept together with the id it was read for, so an answer that lands after the client
  // changed service is recognisably stale rather than silently shown under the new one.
  //   • `offer`         — `{serviceId, service, starts}`, or null (not read yet, or
  //                       cleared for a re-read)
  //   • `timesFailure`  — `{serviceId, error}`: the axios error of the last read, rendered
  //                       through the same refusal table as a submit (a 404 "Service not
  //                       found" included), keyed like `offer` for the same reason
  //   • `timesReload`   — bumped to re-read the same service's times (TIME_UNAVAILABLE)
  //   • `timesNotice`   — the sentence shown ABOVE the times after such a re-read
  const [offer, setOffer] = useState(null);
  const [timesFailure, setTimesFailure] = useState(null);
  const [timesReload, setTimesReload] = useState(0);
  const [timesNotice, setTimesNotice] = useState("");
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  // The picked start: one `{start, held_by_you}` entry of `offer.starts`, or null.
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", notes: "" });
  // Per-field, one i18n key each — populated on submit by validateBookingForm(), cleared
  // as soon as the field it names is edited (so a fixed field's error does not linger
  // once the visitor has acted on it).
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(() => readPending());
  // BUG-019 — the promo code typed for a PRICED appointment. The server revalidates it at
  // payment time (400 PROMO_INVALID): what the page holds here is a preview, not a price.
  const [promoCode, setPromoCode] = useState("");
  // BUG-020 — the gift card typed for a PRICED appointment. Unlike a promo code it does
  // not change the price, only who pays which part of it: the server debits the card at
  // checkout and re-credits it if the session cannot be created.
  const [giftCardCode, setGiftCardCode] = useState("");
  // The OPTIONAL "send me news and offers" box — unticked until the client ticks it
  // (CASL). A tick is consent only once the booking exists (backend crm/consent.py).
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState("");
  // BUG-195 — the start a restored draft (below) named, held until the chosen service's
  // times have loaded so it can be checked against what the server ACTUALLY still offers.
  // STATE, not a ref: `bridgeNonce` resolves asynchronously (it awaits the bridge's own
  // network round trip), which can land either before OR after the times read below has
  // already settled — a ref's mutation would not re-trigger the matching effect in the
  // "already settled" case, silently dropping the restore. State does, in either order.
  const [restoredStart, setRestoredStart] = useState(null);

  // BUG-195 — read (and clear) once `bridgeNonce` answers. A fix-verifier refuted an
  // earlier version of this effect that ran unconditionally on mount: with no check that
  // THIS load was actually a return from the bridge, a second visitor opening the same
  // practice's page on the same browser inside the draft's TTL silently inherited the
  // first one's name/phone/notes. `bridgeNonce` (useRequireAuth.js) is `undefined` until
  // the mount-time bridge check resolves, and `null` — never a value to fall back on —
  // when this mount is NOT a bridge return; only a matching nonce string unlocks the
  // read (lib/pendingBookingDraft.js requires it), so an ordinary page load, first-time
  // or repeat visitor alike, never touches this draft at all. Applied whether or not the
  // Google attempt actually succeeded — a visitor whose attempt failed still gets their
  // typed details back, and can still finish through the email/OTP path without
  // retyping everything. The START is deliberately NOT trusted yet (see the effect
  // below): the row this bug describes is explicit that a stale time must not be
  // silently re-offered.
  useEffect(() => {
    if (!bridgeNonce) return; // undefined (not checked yet) or null (not a bridge return)
    const draft = readAndClearBookingDraft(tenantSlug, bridgeNonce);
    if (!draft) return;
    if (draft.serviceId) setServiceId(draft.serviceId);
    if (draft.form) setForm((f) => ({ ...f, ...draft.form }));
    if (draft.promoCode) setPromoCode(draft.promoCode);
    if (draft.giftCardCode) setGiftCardCode(draft.giftCardCode);
    if (draft.marketingOptIn === true) setMarketingOptIn(true);
    setRestoredStart(draft.start || null);
  }, [bridgeNonce, tenantSlug]);

  // The catalogue decides whether there IS a choice to make, and which of the shapes
  // above this practice has.
  useEffect(() => {
    (async () => {
      let list = [];
      try {
        const { data } = await api.get("/public/booking/services");
        list = Array.isArray(data) ? data.filter((s) => s && s.active !== false) : [];
      } catch {
        list = [];
      }
      // G5 — a per-minute service with no active rate is unbookable (the booking would
      // only ever answer PRICING_NOT_SET for it), so it never reaches the chooser at all
      // rather than being offered and then refused.
      const bookable = list.filter((s) => !isUnbookablePerMinute(s));
      setServices(bookable);
      if (bookable.length === 0) {
        setCatalogue(CATALOGUE.NONE);
      } else if (bookable.length === 1 && !isUnpayableOnline(bookable[0])) {
        // BUG-022 decision 3 — the chooser step is skipped, but the CHOICE is not: the
        // one bookable service is the client's choice as surely as a click would have
        // been (`chosenService` below), and both GET /availability and POST /bookings
        // require its id.
        setCatalogue(CATALOGUE.ONE);
      } else {
        setCatalogue(CATALOGUE.CHOOSER);
      }
    })();
  }, []);

  // THE CHOSEN SERVICE'S OWN catalogue row. On a ONE catalogue it is that one service,
  // whatever `serviceId` says (a restored draft cannot un-choose it). Otherwise only a
  // choosable row counts: an id that names nothing in the bookable catalogue (a draft
  // restored after the service was deactivated) or a service that cannot be paid online
  // is no choice at all, so the chooser stays up rather than a times read that could only
  // fail.
  const chosenService = useMemo(() => {
    if (catalogue === CATALOGUE.ONE) return services[0];
    if (catalogue !== CATALOGUE.CHOOSER) return null;
    return services.find((s) => s.id === serviceId && !isUnpayableOnline(s)) || null;
  }, [catalogue, services, serviceId]);
  const chosenId = chosenService?.id || null;

  // The hold token the pending-payment memo kept, presented ONLY for the service that
  // memo's hold is for: the server adds that held start back for this buyer alone,
  // flagged `held_by_you`, which is what makes an abandoned Stripe tab resumable from
  // the times grid (see lib/pendingBooking.js).
  const holdToken = pending && pending.service_id === chosenId ? pending.hold_token || null : null;

  // ONE READ PER CHOSEN SERVICE. The times are the SERVER's, computed at read time for
  // that service's own length (open_hours.offered_starts): nothing is filtered or folded
  // here, and there is no unfiltered read to fold from — the length of a time IS the
  // service's.
  useEffect(() => {
    if (!chosenId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const params = { service_id: chosenId };
        if (holdToken) params.hold_token = holdToken;
        const { data } = await api.get("/availability", { params });
        if (cancelled) return;
        setOffer({
          serviceId: chosenId,
          service: data?.service || null,
          starts: Array.isArray(data?.starts) ? data.starts : [],
        });
      } catch (e) {
        if (cancelled) return;
        setTimesFailure({ serviceId: chosenId, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, [chosenId, holdToken, timesReload]);

  // The current answer, or null while the chosen service's times are still being read
  // (including an answer for a service the client has since moved away from).
  // A re-read (`timesReload`) clears `offer` first, so it too reads as in flight, and a
  // failure only counts until the next answer for the same service replaces it.
  const current = offer && offer.serviceId === chosenId ? offer : null;
  const failure = !current && timesFailure && timesFailure.serviceId === chosenId
    ? timesFailure.error : null;
  const timesLoading = Boolean(chosenId) && !current && !failure;
  // THE SERVICE THE TIMES STEP DESCRIBES: the one the times read answered with — fresher
  // than the chooser's row, and from the SAME read as the starts, so the length, price
  // and delivery mode stated beside a time are the ones that time was computed for. The
  // chooser's row only ever stands in for the NAME (`serviceName` below).
  const offered = current?.service || null;
  const offerable = isOfferable(offered);
  const starts = useMemo(() => (offerable ? current.starts : []), [offerable, current]);

  // Choosing (or re-choosing) a service invalidates every downstream selection: the day
  // that was highlighted may hold no times for the new service, and the start that was
  // selected certainly belongs to the old one.
  const chooseService = (id) => {
    setServiceId(id);
    setSelectedDayKey(null);
    setSelected(null);
    setError("");
    setTimesNotice("");
  };

  const backToServices = () => {
    // Back to an unanswered chooser — only reachable on a CHOOSER catalogue, where
    // clearing the chosen id is exactly what unsettles the choice again.
    chooseService(null);
  };

  const multiService = catalogue === CATALOGUE.CHOOSER;

  // BUG-195 — resolves the restored start against the FRESH times of the restored
  // service, whichever of the two answers last. At most once: cleared on its first
  // check, so a later re-read of the times is never re-matched against a decision
  // already made once. A restore that can never be checked — the catalogue settled and
  // the draft's service is not choosable any more, or its times could not be read — is
  // dropped the same way, never left pending.
  useEffect(() => {
    if (!restoredStart || catalogue === CATALOGUE.LOADING) return;
    if (timesLoading) return;
    const wanted = restoredStart;
    setRestoredStart(null);
    const found = starts.find((s) => sameInstant(s.start, wanted));
    if (found) {
      setSelected(found);
    } else {
      // The same sentence a submit refused with TIME_UNAVAILABLE shows — one message for
      // "that time is gone", however the page learned it. A toast, not the inline
      // `error` box: that box only renders once a time IS selected (STEP 2), and there
      // is deliberately no time to select here.
      toast.info(t("booking.timeUnavailable"));
    }
  }, [restoredStart, catalogue, timesLoading, starts, t]);

  // Group by viewer-LOCAL date key; each day's starts sorted ascending.
  const startsByDay = useMemo(() => {
    const map = {};
    for (const s of starts) {
      const key = format(new Date(s.start), DAY_KEY_FMT);
      (map[key] = map[key] || []).push(s);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.start) - new Date(b.start)));
    return map;
  }, [starts]);

  const sortedDayKeys = useMemo(() => Object.keys(startsByDay).sort(), [startsByDay]);
  const availableDays = useMemo(() => new Set(sortedDayKeys), [sortedDayKeys]);

  // Select the first available day (and its month) once times load — and again when a
  // re-read (TIME_UNAVAILABLE) emptied the day that was selected, so the client is never
  // left looking at a day with nothing on it while other days still have times.
  useEffect(() => {
    if (!sortedDayKeys.length) return;
    if (selectedDayKey && availableDays.has(selectedDayKey)) return;
    const first = sortedDayKeys[0];
    setSelectedDayKey(first);
    setCalendarMonth(startOfMonth(parse(first, DAY_KEY_FMT, new Date())));
  }, [sortedDayKeys, availableDays, selectedDayKey]);

  const selectedDateObj = selectedDayKey ? parse(selectedDayKey, DAY_KEY_FMT, new Date()) : undefined;
  const todayMonthStart = startOfMonth(new Date());
  const lastMonthStart = sortedDayKeys.length
    ? startOfMonth(parse(sortedDayKeys[sortedDayKeys.length - 1], DAY_KEY_FMT, new Date()))
    : todayMonthStart;
  const endMonth = lastMonthStart < todayMonthStart ? todayMonthStart : lastMonthStart;

  const dayStarts = selectedDayKey ? (startsByDay[selectedDayKey] || []) : [];

  const serviceName = offered?.name || chosenService?.name || t("booking.serviceFallback");
  const durationMin = offered?.duration_min;

  // WHERE IT HAPPENS, ON EVERY STEP THE CLIENT PASSES THROUGH. The chooser CARD carries
  // this badge already, but the chooser only renders when there is a choice to make: a
  // practice that sells one video consultation showed the client nothing, anywhere, that
  // said they would not be travelling to the practice — not on the times list, not on the
  // summary they pay from, not in the confirmation. Read off the times read's own service.
  const isVideo = offered?.delivery_mode === "video";

  // Split on the {date} token ourselves (rather than i18n.interpolate) so only the
  // date substring gets the `capitalize` class — the surrounding phrase ("Times
  // for" / "Créneaux du") must not be title-cased along with it.
  const [timesForBefore, timesForAfter] = t("booking.timesFor").split("{date}");

  // BUG-193 — the disabled/modifier/locale/classNames wiring itself lives in
  // AvailabilityCalendar (shared with public/ManageBooking.jsx's reschedule step); it
  // validates `availableDays` itself before ever calling this, so this only has to act
  // on an already-confirmed choice.
  const onDaySelect = (key) => {
    setSelectedDayKey(key);
    setSelected(null);
    setError("");
  };

  // Is the chosen service priced? That decides the promo/gift instruments, the button
  // copy and the "what happens next" line — never WHETHER the visitor may pay. The
  // practice's own Stripe onboarding is not consulted: Solunary is the merchant of
  // record, so the platform collects and the practitioner's share waits in the ledger.
  const priced = priceCentsOf(offered) > 0;
  // G5 — a per-minute consult: no Stripe charge happens here at all (price_cents stays 0,
  // so `priced` is already false for it), the button leads to card authorisation rather
  // than a charge, and promo/gift codes never apply (Flow C intents take no promo).
  const perMinute = isPerMinute(offered);

  // WHAT THIS APPOINTMENT WILL ACTUALLY COST (BUG-054). The summary used to print the
  // service's stored price and nothing else, so a practice with tax lines configured sent
  // its client to a payment page asking for more than the page had shown. The tax is the
  // server's (`platform_helpers.compute_tax_split`, through
  // GET /api/public/checkout-breakdown) — never arithmetic here — and so is the discount
  // once a promo code has been accepted.
  //
  // A VIDEO appointment is quoted as `telehealth`, an in-person one as `booking`: the two
  // are different product natures to the commission engine and may carry different tax
  // treatment (platform_helpers.OFFERING_PRODUCT_TYPES). A per-minute consult is not
  // quoted at all — its taxable base is not known until the call ends, and
  // `ConsultRateLabel`/`ConsultCeilingDisclosure` own that disclosure.
  const { quote: bookingQuote, error: bookingQuoteError } = useCheckoutQuote({
    serviceType: isVideo ? "telehealth" : "booking",
    offeringType: isVideo ? "telehealth" : "booking",
    offeringId: offered?.id || "",
    amountCents: priceCentsOf(offered),
    tenantSlug,
    promoCode,
    enabled: Boolean(selected && priced && !perMinute),
  });

  // A field's error is a statement about its PAST value at submit time — it must not
  // outlive the next keystroke in that same field, or a visitor who has already fixed
  // the typo keeps reading a stale complaint until their next submit attempt.
  const updateField = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((fe) => {
      if (!fe[field]) return fe;
      const next = { ...fe };
      delete next[field];
      return next;
    });
  };

  const book = async () => {
    // Caught here, before the auth gate and before the round trip: a blank lastName or a
    // malformed email is something THIS form can answer itself, and must never reach a
    // 422 (see admin/booking/ServicesAdmin.jsx:70-85 for the same principle on the admin
    // side). `!user`: email is only the visitor's own responsibility pre-authentication —
    // once signed in, the payload's email is the signed-in identity (checkoutEmail()
    // below), never this (by-then-disabled) field.
    const errors = validateBookingForm(form, !user);
    if (errors) { setFieldErrors(errors); return; }
    setFieldErrors({});
    // BUG-195 — the ONE hook `ensureAuthed` offers for "about to be navigated away from":
    // it fires only if the visitor actually picks the tenant-host Google bridge, right
    // before that navigation, carrying the nonce THIS bridge attempt minted
    // (lib/authBridge.js's `onNonceReady`) — never on the OTP path or the platform-host
    // popup (neither one loses this state to begin with). `form` is passed whole;
    // `writeBookingDraft` itself keeps only firstName/lastName/phone/notes and drops
    // `email` — the whole point is that the email gets replaced by the signed-in
    // identity, never resumed from what was typed before the redirect.
    const captureBookingDraft = (nonce) => {
      writeBookingDraft({
        tenantSlug, nonce, serviceId: chosenId, start: selected?.start || null, form,
        promoCode, giftCardCode, marketingOptIn,
      });
    };
    // The typed address is handed over so the gate can say which one the purchase
    // will actually use when they differ (BUG-052) — the substitution itself is
    // `checkoutEmail`'s and stays unchanged.
    const u = await ensureAuthed(form.email, captureBookingDraft);
    if (!u) return;
    if (!selected || !offered) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/bookings/v2", {
        ...form,
        // The signed-in identity always wins over whatever the (now disabled)
        // email field holds — see checkoutEmail() above.
        email: checkoutEmail(u, form.email),
        // WHAT and WHEN. The server recomputes whether this start is still offered for
        // this service (409 TIME_UNAVAILABLE otherwise) — the page's list is a view, the
        // claims are the authority. `start` is sent back exactly as the server spelled it.
        service_id: offered.id,
        start: selected.start,
        locale: lang,
        // The server decides the return HOST (tenant/platform allowlist); the page only
        // says where it lives. Ignored for a free appointment.
        origin_url: window.location.origin,
        ...checkoutReturnFields("/merci/reservation", "/reserver"),
        // Absent when nothing was typed: the server reads an absent code as "no promo",
        // whereas an empty string would be a value to interpret. Ignored for a free
        // appointment too (nothing to discount).
        ...(promoCode && priced ? { promo_code: promoCode } : {}),
        ...(giftCardCode && priced ? { gift_card_code: giftCardCode } : {}),
        // Attribution (contract §2): the referral code the visitor arrived with, or "".
        // Sent on every capturable checkout so the sale can be traced back to whoever
        // introduced the buyer; the server resolves it and silently ignores an invalid,
        // self-referring or expired one — a checkout never fails on a query parameter the
        // buyer did not choose.
        ref: currentRef(),
        // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
        promotion_id: currentPromotionRef() || undefined,
        // --- crm-module END ---
        // The client's own answer to the optional marketing box, false when untouched.
        marketing_opt_in: marketingOptIn,
      });
      // Flow C exit (modules/booking/service.py create_booking): a per-minute service
      // bypasses the ordinary checkout entirely — the time is held but NO Stripe object
      // exists yet, so there is no `checkout_url` to branch on below. `consult`/
      // `pricing_mode` are BOTH in the payload for exactly this check; a money field
      // (there is none here, on purpose) would be the wrong thing to sniff for. The
      // disclosure + consent step happens on `/consultation/confirmer` — this only
      // routes there.
      //
      // Deliberately NOT seeding ConsultBooking.jsx's disclosure from this response to
      // skip its loading spinner: this payload has no `reschedule_min_notice_hours`
      // (only `no_show_fee_cents`), and `ConsultCeilingDisclosure` renders both from one
      // quote object. A seed missing one field would render a broken interpolation for
      // a moment on the one screen this whole campaign is about getting exactly right.
      // `/quote` is the sole authority and ConsultBooking.jsx fetches it itself; a short
      // spinner is the honest cost of that.
      if (data?.consult === true || data?.pricing_mode === "per_minute") {
        navigate(
          tenantHref(
            `/consultation/confirmer?appointmentId=${encodeURIComponent(data.booking_id)}` +
            `&token=${encodeURIComponent(data.manage_token)}`,
          ),
        );
        return;
      }
      if (data?.checkout_url) {
        // Priced: the time is HELD server-side and the buyer leaves to pay. No "booked"
        // screen here — the appointment only exists once the money arrives.
        //
        // The payment IN PROGRESS is remembered before leaving (lib/pendingBooking.js),
        // because the hold takes the time away from every read, the buyer's own included.
        // `until` is the SERVER's deadline for this payment page (`expires_at`, echoed
        // unchanged on a resume): a banner anchored to a local guess outlived the session
        // it pointed at. With no readable deadline there is nothing honest to promise, so
        // no memo is written and the buyer simply goes to pay.
        const until = Date.parse(data.expires_at);
        if (Number.isFinite(until)) {
          writePending({
            service_id: offered.id, start: selected.start, hold_token: data.hold_token || null,
            email: checkoutEmail(u, form.email), url: data.checkout_url,
            label: offered.name || "", until,
          });
        }
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data);
      clearPending();
      setPending(null);
    } catch (e) {
      // BUG-208 — ONE reader for every shape a refusal can arrive in (bare string,
      // `{code, message}` envelope, Pydantic 422 list, or no body at all).
      const message = bookingRefusalMessage(e, t, tc);
      if (TIMES_REFRESH_CODES.has(refusalCode(e))) {
        // The picked time is gone: back to the times, re-read from the server, with the
        // reason stated where the client now has to act. The typed details are kept.
        setSelected(null);
        setTimesNotice(message);
        setOffer(null);
        setTimesFailure(null);
        setTimesReload((n) => n + 1);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  // THE TIMES STEP'S BODY when there is nothing to pick from: the read failed, the
  // service turned unbookable since the chooser loaded, or it simply has no open time.
  const timesDeadEnd = failure
    ? bookingRefusalMessage(failure, t, tc)
    : current && !offerable
      ? t("booking.serviceUnavailable")
      : current && starts.length === 0
        ? t("booking.noSlots")
        : null;

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 max-w-5xl mx-auto px-6">
        <span className="eyebrow">{t("booking.eyebrow")}</span>
        <h1 className="font-display text-5xl md:text-6xl text-eh-ink mt-3 leading-tight">
          {t("booking.heading")}
        </h1>
        <p className="mt-4 text-lg text-eh-muted max-w-2xl">
          {t("booking.subheading")}
        </p>

        {pending && !done && (
          <div
            data-testid="booking-resume"
            className="mt-6 rounded-2xl border border-eh-ochre-deep/40 bg-eh-cream/70 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="text-sm text-eh-ink">
              <b>{t("booking.resumeTitle")}</b>
              <span className="block text-eh-muted mt-0.5">
                {t("booking.resumeBody", { service: pending.label })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <a href={pending.url} data-testid="booking-resume-pay" className="btn-primary text-sm">
                {t("booking.resumePay")}
              </a>
              <button
                type="button"
                data-testid="booking-resume-dismiss"
                onClick={() => {
                  clearPending();
                  setPending(null);
                }}
                className="text-xs text-eh-muted underline"
              >
                {t("booking.resumeDismiss")}
              </button>
            </div>
          </div>
        )}

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 bg-eh-cream border border-eh-border-soft rounded-3xl p-10 text-center"
            data-testid="booking-confirmation"
          >
            <CheckCircle2 size={42} className="mx-auto text-eh-sage-deep" />
            <h2 className="font-display text-3xl mt-4">{t("booking.confirmTitle", { name: done.firstName })}</h2>
            <p className="text-eh-muted mt-3">
              {t("booking.confirmBody")}
            </p>
            <a href={homePath} data-testid="booking-back-home" className="btn-primary text-sm mt-8 inline-flex">{t("booking.backHome")}</a>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 bg-white border border-eh-border-soft rounded-2xl overflow-hidden"
          >
            {catalogue === CATALOGUE.LOADING || timesLoading ? (
              <div className="p-10 flex items-center gap-2 text-eh-muted" data-testid="booking-loading">
                <Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}
              </div>
            ) : catalogue === CATALOGUE.NONE ? (
              // Nothing is bookable (see CATALOGUE above): no chooser, no times grid, and
              // no times read — it requires a service, and there is none to name.
              <div className="p-10 text-center text-eh-muted text-sm" data-testid="no-bookable-service">
                {t("booking.noBookableService")}
              </div>
            ) : !chosenService ? (
              // STEP 0 — the service.
              <div className="p-6 lg:p-10" data-testid="service-chooser">
                <h3 className="font-display text-2xl text-eh-ink">{t("booking.chooseService")}</h3>
                <p className="text-sm text-eh-muted mt-2">{t("booking.chooseServiceHelp")}</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {(services || []).map((s) => {
                    const unpayable = isUnpayableOnline(s);
                    return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => { if (!unpayable) chooseService(s.id); }}
                      disabled={unpayable}
                      aria-disabled={unpayable}
                      data-testid={`service-choice-${s.id}`}
                      className={`text-left bg-eh-cream border border-eh-border-soft rounded-2xl px-5 py-4 transition ${unpayable ? "opacity-60 cursor-not-allowed" : "hover:border-eh-ink"}`}
                    >
                      <span className="block font-medium text-eh-ink">{s.name}</span>
                      <span className="mt-1 flex items-center gap-3 text-xs text-eh-muted">
                        {/* THE SERVICE'S OWN LENGTH: under open hours it is exactly what
                            the appointment occupies (plus the practice's rest after it),
                            so the tile and every time it leads to state one number. */}
                        {s.duration_min > 0 && (
                          <span className="inline-flex items-center gap-1" data-testid={`service-duration-${s.id}`}>
                            <Clock size={12} /> {s.duration_min} {t("booking.minutesSuffix")}
                          </span>
                        )}
                        {/* WHERE IT HAPPENS, on the card where the client chooses. A
                            consultation delivered by video is priced and paid exactly like
                            any other service — nothing about the money says so — and a
                            client who booked one expecting to travel to the practice, or
                            the reverse, finds out at the appointment. */}
                        {s.delivery_mode === "video" && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-eh-cream text-eh-ink"
                            data-testid={`service-video-${s.id}`}
                          >
                            <Video size={12} /> {t("booking.videoBadge")}
                          </span>
                        )}
                        {isPerMinute(s) ? (
                          <ConsultRateLabel consult={consultOf(s)} testId={`service-rate-${s.id}`} />
                        ) : (
                          <span className="font-medium text-eh-ink">
                            {s.price_cents > 0
                              ? fmtPrice(s.price_cents, s.currency, lang)
                              : t("booking.freeLabel")}
                          </span>
                        )}
                      </span>
                      {unpayable && (
                        // The same money-path refusal copy the storefronts use (checkoutErrors.js
                        // INVALID_PRICE): "this price cannot be paid online", already in 3 locales.
                        <span className="mt-1 block text-xs italic text-eh-muted" data-testid={`service-unavailable-${s.id}`}>
                          {t("checkout.error.invalidPrice")}
                        </span>
                      )}
                    </button>
                    );
                  })}
                </div>
              </div>
            ) : timesDeadEnd ? (
              <div className="p-10 text-center text-eh-muted text-sm" data-testid="booking-no-times">
                {timesDeadEnd}
                {/* A chosen service with no open times must not be a cul-de-sac: the
                    practice may well have hours for its other ones. */}
                {multiService && (
                  <button
                    type="button"
                    onClick={backToServices}
                    data-testid="booking-change-service-empty"
                    className="block mx-auto mt-4 text-sm text-eh-ink underline"
                  >
                    {t("booking.changeService")}
                  </button>
                )}
              </div>
            ) : !selected ? (
              <div className="flex flex-col lg:grid lg:grid-cols-[220px_1fr_300px]">
                {/* LEFT rail — service / length / price / timezone. Every time on the
                    right is for THIS service, so what it costs and how long it lasts is
                    stated once, here, rather than repeated on each time. */}
                <div className="p-6 lg:p-8 lg:border-r border-b lg:border-b-0 border-eh-border-soft">
                  <h3 className="font-display text-xl text-eh-ink">{serviceName}</h3>
                  {durationMin > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-eh-muted" data-testid="booking-rail-duration">
                      <Clock size={14} /> {durationMin} {t("booking.minutesSuffix")}
                    </div>
                  )}
                  <div className="mt-2 text-sm">
                    {perMinute ? (
                      <ConsultRateLabel consult={consultOf(offered)} testId="booking-rail-price" />
                    ) : (
                      <span className="font-medium text-eh-ink" data-testid="booking-rail-price">
                        {priced
                          ? fmtPrice(priceCentsOf(offered), offered.currency, lang)
                          : t("booking.freeLabel")}
                      </span>
                    )}
                  </div>
                  {isVideo && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-eh-ink"
                      data-testid="booking-rail-video"
                    >
                      <Video size={14} /> {t("booking.videoBadge")}
                    </div>
                  )}
                  <div className="mt-4 flex items-start gap-2 text-xs text-eh-muted">
                    <Globe size={13} className="mt-0.5 shrink-0" />
                    <span>{t("booking.timezoneNote", { tz })}</span>
                  </div>
                  {multiService && (
                    <button
                      type="button"
                      onClick={backToServices}
                      data-testid="booking-change-service"
                      className="mt-4 text-xs text-eh-ink underline"
                    >
                      {t("booking.changeService")}
                    </button>
                  )}
                </div>

                {/* MIDDLE — month calendar */}
                <div className="p-4 lg:p-6 lg:border-r border-b lg:border-b-0 border-eh-border-soft flex flex-col items-center justify-center">
                  <AvailabilityCalendar
                    Calendar={Calendar}
                    availableDays={availableDays}
                    lang={lang}
                    selectedDate={selectedDateObj}
                    onSelectDay={onDaySelect}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    startMonth={todayMonthStart}
                    endMonth={endMonth}
                  />
                </div>

                {/* RIGHT — the selected day's start times */}
                <div className="p-6 lg:p-8">
                  {timesNotice && (
                    <div data-testid="booking-times-notice" className="mb-4 text-sm text-red-600">
                      {timesNotice}
                    </div>
                  )}
                  <h4 className="font-display text-base text-eh-ink mb-4">
                    {selectedDayKey ? (
                      <>
                        {timesForBefore}
                        <span className="capitalize">{fmtDayLong(selectedDateObj, lang)}</span>
                        {timesForAfter}
                      </>
                    ) : t("booking.pickDate")}
                  </h4>
                  {dayStarts.length === 0 ? (
                    <div className="text-sm text-eh-muted">{t("booking.noTimes")}</div>
                  ) : (
                    <div data-testid="slots-grid" className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                      {dayStarts.map((s) => (
                        <button
                          key={s.start}
                          onClick={() => { setSelected(s); setError(""); setTimesNotice(""); }}
                          data-testid={`time-${s.start}`}
                          className={`w-full text-left border rounded-xl px-4 py-3 transition flex items-center justify-between gap-3 ${s.held_by_you ? "bg-eh-cream border-eh-ochre-deep" : "bg-eh-cream border-eh-border-soft hover:border-eh-ink"}`}
                        >
                          <span className="font-medium">{fmtTime(s.start, lang)}</span>
                          <span className="flex items-center gap-2">
                            {/* The one start the server added back for THIS buyer: their
                                unpaid hold occupies it, so nobody else is offered it.
                                Clicking it and submitting again resumes that payment. */}
                            {s.held_by_you && (
                              <span className="text-xs font-medium text-eh-ink" data-testid={`time-held-${s.start}`}>
                                {t("booking.heldByYou")}
                              </span>
                            )}
                            <ArrowRight size={13} className="text-eh-muted" />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // STEP 2 — summary + form
              <div className="p-6 lg:p-10 max-w-xl mx-auto">
                <button onClick={() => { setSelected(null); setError(""); }} className="btn-ghost text-sm mb-6">
                  <ArrowLeft size={14} /> {tc("actions.back")}
                </button>
                <div className="bg-eh-cream border border-eh-border-soft rounded-2xl p-5 mb-6">
                  <div className="font-display text-lg capitalize">{fmtDayLong(selected.start, lang)}</div>
                  <div className="text-sm text-eh-muted mt-1" data-testid="booking-summary-line">
                    {/* The length is the SERVICE's — exactly what the appointment occupies
                        (open-hours rule), so it cannot disagree with the tile it came from. */}
                    {fmtTime(selected.start, lang)} · {serviceName}
                    {durationMin > 0 ? ` · ${durationMin} ${t("booking.minutesSuffix")}` : ""}
                  </div>
                  {isVideo && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-sm text-eh-ink"
                      data-testid="booking-summary-video"
                    >
                      <Video size={14} /> {t("booking.videoBadge")}
                    </div>
                  )}
                  {perMinute ? (
                    <div className="mt-2">
                      <ConsultRateLabel consult={consultOf(offered)} testId="booking-price" />
                    </div>
                  ) : priced && (
                    <>
                      <div className="text-sm font-medium text-eh-ink mt-2" data-testid="booking-price">
                        {fmtPrice(priceCentsOf(offered), offered.currency, lang)}
                      </div>
                      {/* The tax lines and the total the payment page will ask for
                          (BUG-054). `compact`: the price is the line above, so the
                          breakdown adds what was missing rather than repeating it. */}
                      <div className="mt-2">
                        <PriceBreakdown
                          quote={bookingQuote}
                          error={bookingQuoteError}
                          currency={offered.currency}
                          compact
                          testId="booking-breakdown"
                        />
                      </div>
                    </>
                  )}
                </div>
                <h3 className="font-display text-2xl mb-4">{t("booking.yourInfo")}</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input data-testid="book-firstname" placeholder={t("booking.firstNamePh")} maxLength={80} value={form.firstName} onChange={updateField("firstName")} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" />
                      <FieldError message={fieldErrors.firstName ? t(fieldErrors.firstName) : ""} testId="book-firstname-error" />
                    </div>
                    <div>
                      <input data-testid="book-lastname" placeholder={t("booking.lastNamePh")} maxLength={80} value={form.lastName} onChange={updateField("lastName")} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" />
                      <FieldError message={fieldErrors.lastName ? t(fieldErrors.lastName) : ""} testId="book-lastname-error" />
                    </div>
                  </div>
                  <div>
                    <input
                      data-testid="book-email"
                      type="email"
                      placeholder={t("booking.emailPh")}
                      maxLength={254}
                      value={user ? user.email : form.email}
                      onChange={updateField("email")}
                      disabled={!!user}
                      className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm disabled:opacity-70"
                    />
                    <FieldError message={fieldErrors.email ? t(fieldErrors.email) : ""} testId="book-email-error" />
                  </div>
                  {user && (
                    <p className="text-xs text-eh-muted">
                      {t("clientAuth.signedInAs", { email: user.email })}{" "}
                      <button type="button" onClick={() => logout()} className="underline">
                        {t("clientAuth.notYou")}
                      </button>
                    </p>
                  )}
                  <div>
                    <input data-testid="book-phone" type="tel" placeholder={t("booking.phonePh")} maxLength={40} value={form.phone} onChange={updateField("phone")} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" />
                    <FieldError message={fieldErrors.phone ? t(fieldErrors.phone) : ""} testId="book-phone-error" />
                  </div>
                  <div>
                    <textarea data-testid="book-notes" placeholder={t("booking.notesPh")} rows={3} maxLength={2000} value={form.notes} onChange={updateField("notes")} className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm resize-none" />
                    <FieldError message={fieldErrors.notes ? t(fieldErrors.notes) : ""} testId="book-notes-error" />
                  </div>
                  {/* PRICED, NON-PER-MINUTE SERVICES ONLY. A free appointment has nothing
                      to discount, and offering a code there would invite a buyer to type
                      one that the server then ignores. A per-minute consult prices
                      through its own quote/intent, which takes no promo at all. */}
                  {priced && !perMinute && (
                    <PromoField
                      kind="booking"
                      value={promoCode}
                      onChange={setPromoCode}
                      currency={offered.currency}
                      disabled={busy}
                    />
                  )}
                  {priced && !perMinute && (
                    <GiftCardField
                      value={giftCardCode}
                      onChange={setGiftCardCode}
                      // THE TAXED TOTAL, not the service's shelf price: the card is a
                      // tender against what will be charged. Quoting it against the
                      // pre-tax figure understated what it covers. `0` while the quote is
                      // unknown, which puts the field in its balance-only mode rather
                      // than inventing a residual.
                      amountCents={bookingQuote?.total_charged_cents || 0}
                      currency={offered.currency}
                      disabled={busy}
                    />
                  )}
                  <MarketingOptIn
                    form="booking"
                    checked={marketingOptIn}
                    onChange={setMarketingOptIn}
                    disabled={busy}
                  />
                  {/* Testid'd so the refusal the patient reads is addressable — by
                      Reserver.refusalIsExplained.test.js and by a browser QA pass.
                      Before it existed, the only way to assert on this message was
                      `container.textContent`, which cannot tell the message apart from
                      the rest of the form. */}
                  {error && (
                    <div data-testid="booking-error" className="text-sm text-red-600">{error}</div>
                  )}
                  <button
                    onClick={book}
                    // Content validity (blank/invalid fields) is judged on click, by
                    // validateBookingForm() inside book() — a disabled button gives no
                    // reason a visitor could act on, so only STRUCTURAL blockers (no
                    // time chosen, a request already in flight) disable it here.
                    disabled={!selected || busy}
                    data-testid="book-submit"
                    className="btn-primary text-sm w-full justify-center disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}{" "}
                    {perMinute
                      ? t("consult.chooser.submit")
                      : priced ? t("booking.payButton") : t("booking.confirmButton")}
                  </button>
                  {/* WHAT HAPPENS NEXT, and it differs by path. A paid appointment is
                      confirmed the moment Stripe reports the payment — `fulfil_booking`
                      sets `confirmed` with no human step — so promising an approval, as
                      the page heading used to do for everybody, described a review that
                      never happens. A free appointment really is a request the
                      practitioner accepts. A per-minute consult is neither: nothing is
                      confirmed here at all — this button only leads to the ceiling
                      disclosure and the card authorisation, so it shares the "paid" copy
                      rather than the "wait for approval" one. */}
                  <p className="text-xs text-eh-muted text-center" data-testid="book-next-step">
                    {(priced || perMinute) ? t("booking.nextStepPaid") : t("booking.nextStepFree")}
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </section>
      {authDialog}
    </ThemedPageLayout>
  );
}
